import { select } from 'd3-selection';
import { timeInterval } from 'd3-time';
import { Transition } from 'd3-transition';

const MAX_LENGTH = 12;

export interface Rect {
    width?: any;
    height?: any;
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class Util {
    static _wrapText(
        text: string,
        maxLines: number,
        maxLength: number = MAX_LENGTH,
        concatLast: boolean = true,
    ): string[] {
        const words = text.split(' ');
        let lines = [];
        let lineLength = 0;
        words.forEach((value, index) => {
            if (value.trim().length === 0) return;
            lines.push(value);
            lineLength += value.length;
            if (index === words.length - 1) return;
            if (
                lineLength >= maxLength ||
                (index + 1 <= words.length &&
                    lineLength + words[index + 1].length >= maxLength)
            ) {
                lines.push('<br>');
                lineLength = 0;
            } else {
                lines.push(' ');
                lineLength++;
            }
        });
        lines = lines.join('').split('<br>');
        if (!concatLast) {
            lines = lines.slice(0, maxLines);
        } else {
            if (lines.length > maxLines) {
                const other = lines.slice(maxLines);
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                lines[maxLines - 1] += ` ${other.join(' ')}`;
                lines = lines.slice(0, maxLines);
            }
        }
        return lines;
    }

    static wrapText(
        el: Element,
        height: number,
        stack: 'middle' | 'top',
        maxLength: number = MAX_LENGTH,
    ): void {
        const lineHeight = el.getBoundingClientRect().height;
        const maxLines = Math.floor(height / lineHeight);
        if (maxLines < 2) return;
        const node = select(el);
        const lines = Util._wrapText(node.text(), maxLines, maxLength);
        if (lines.length < 2) return;
        node.text('');
        const x = node.attr('x') || 0;
        const y = node.attr('y') || 0;
        const dy = node.attr('dy') || 0;
        let j;
        if (stack === 'middle') {
            j = -Math.floor(lines.length / 2);
            lines.forEach((line, index) => {
                node.append('tspan')
                    .attr('x', x)
                    .attr(
                        'y',
                        index === 0
                            ? `${Math.floor(
                                  (height - lineHeight * maxLines) / 2,
                              )}px`
                            : null,
                    )
                    .attr(
                        'dy',
                        index === 0
                            ? `${lineHeight * j + lineHeight / 2}px`
                            : `${lineHeight}px`,
                    )
                    .text(line);
                j++;
            });
        }
        if (stack === 'top') {
            j = 0;
            lines.forEach((line, index) => {
                node.append('tspan')
                    .attr('x', x)
                    .attr('y', index === 0 ? y : null)
                    .attr('dy', index === 0 ? dy : `${lineHeight}px`)
                    .text(line);
                j++;
            });
        }
    }

    static fitText(
        playGround: any,
        text: string,
        maxWidth: number,
        maxHeight: number,
        minFontSize: number = 0,
        maxFontSize: number = 1000,
    ): number {
        let fontSize = minFontSize == null ? 0 : minFontSize;
        while (fontSize < maxFontSize) {
            fontSize++;
            const textG = playGround
                .append('text')
                .attr('font-size', fontSize)
                .text(text);
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const rect = (<HTMLElement>textG.node()).getBoundingClientRect();
            textG.remove();
            if (rect.width >= maxWidth || rect.height >= maxHeight) break;
        }
        return Math.min(fontSize, maxFontSize);
    }

    static firstBreak(
        playGround: any,
        text: string,
        maxWidth: number,
        fontSize: number = -1,
    ): number {
        const chars = text.split('');
        let line = '';
        for (let i = 0; i < chars.length; i++) {
            line += chars[i];
            const textG = playGround.append('text').text(line);
            if (fontSize > 0) {
                textG.attr('font-size', `${fontSize}px`);
            }
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const width = (<HTMLElement>textG.node()).getBoundingClientRect()
                .width;
            textG.remove();
            if (width > maxWidth) break;
        }
        return line.length;
    }

    static xRotate(el: HTMLElement, rotate: number): void {
        const rect = el.getBoundingClientRect();
        select(el).attr(
            'transform',
            `translate(-${rect.width / 2}, ${
                rect.height / 2
            }) rotate(${rotate} ${rect.width / 2} ${rect.height / 2})`,
        );
    }

    static fillText(
        text: string,
        maxLines: number,
        maxLength: (lineNum: number) => number,
    ): string[] {
        let lines = [];
        let remainingText = text;
        for (let i = 1; i <= maxLines; i++) {
            const length = maxLength(lines.length + 1);
            lines = lines.concat(
                Util._wrapText(remainingText, 1, length, i === maxLines),
            );
            remainingText = remainingText.substr(lines.join(' ').length);
            if (remainingText.length === 0) break;
        }
        return lines;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    static fontSizeLineHeight(playGround: any, fontSize: number = -1) {
        const textG = playGround.append('text').text('lorem');
        if (fontSize > 0) {
            textG.attr('font-size', `${fontSize}px`);
        }
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const height = (<HTMLElement>textG.node()).getBoundingClientRect()
            .height;
        textG.remove();
        return height * 1.15;
    }

    static generateSRId(id: string): string {
        const srId = (Math.random() * 1000).toFixed().toString();
        return `${id}SR${srId}`;
    }

    static findSRIds(root: HTMLElement, id: string): string[] {
        const els = root.querySelectorAll(`[id^="${id}SR"]`);
        return Array.prototype.slice
            .call(els)
            .map((value) => value.getAttribute('id'));
    }

    static applySRStyles(selection: any): void {
        selection
            .style('pointer-events', 'none')
            .style('-webkit-user-select', 'none')
            .style('-moz-user-select', 'none')
            .style('-ms-user-select', 'none')
            .style('user-select', 'none');
    }

    static endAll(transition: Transition<any, any, any, any>, cb): void {
        if (!cb) return;
        if (transition.empty()) {
            cb();
            return;
        }

        let s = transition.size();
        transition.on('end', () => {
            s--;
            if (s === 0) {
                cb();
            }
        });
    }
}

// https://github.com/d3/d3-time/blob/master/src/month.js
export const timeMonthEnd = timeInterval(
    function (date) {
        date.setDate(1);
        date.setMonth(date.getMonth() + 1);
        date.setDate(-1);
        date.setHours(23, 59, 59, 999);
        console.log(date);
    },
    function (date, step) {
        date.setMonth(date.getMonth() + step);
    },
    function (start, end) {
        return (
            end.getMonth() -
            start.getMonth() +
            (end.getFullYear() - start.getFullYear()) * 12
        );
    },
    function (date) {
        return date.getMonth();
    },
);

export const pluck = <T>(obj: any, path: string): T => {
    const arrPath = path.split('.');
    return arrPath.reduce((p: any, c) => {
        if (!p) return p;
        const isArr = Array.isArray(p);
        const concatArr = c.endsWith('[]');
        if (isArr || concatArr) {
            const k = c.replace('[]', '');
            if (isArr) {
                return p.reduce((pp, cc) => {
                    if (concatArr) {
                        pp.push(...cc[k]);
                    } else {
                        pp.push(cc[k]);
                    }
                    return pp;
                }, []);
            } else {
                return p[k];
            }
        }
        return p[c];
    }, obj);
};

export const dedup = <T>(
    arr: T[],
    lookupKey: (value: T) => string | number,
): T[] => {
    if (!arr) {
        return arr;
    }
    const lookup = {};
    return arr.reduce((p, c) => {
        const key = lookupKey(c);
        if (!lookup[key]) {
            lookup[key] = true;
            p.push(c);
        }
        return p;
    }, []);
};
